import { useState } from 'react';

import { accentColor, type AuthFeature } from './auth-config';
import { FeatureIcon } from './auth-icons';

interface FeatureCardProps {
    feature: AuthFeature;
    accentHue: number;
}

/**
 * Compact feature card with a hue-driven hover lift. The colour-bar at
 * the bottom expands on hover (sweep effect from the source design).
 *
 * Base chrome (background, text, border) uses Tailwind tokens so the
 * card flips with the theme automatically. Only the accent-tinted
 * border, shadow and sweep bar use `accentHue`.
 */
export function FeatureCard({ feature, accentHue }: FeatureCardProps) {
    const [hover, setHover] = useState(false);

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="group relative flex-1 overflow-hidden rounded-2xl border border-border bg-card/80 px-5 pt-5 pb-[22px] shadow-sm backdrop-blur transition-all duration-[220ms] hover:-translate-y-0.5 hover:bg-accent/30 hover:shadow-lg"
            style={{
                borderColor: hover ? accentColor(60, 0.16, 0.4, accentHue) : undefined,
                boxShadow: hover
                    ? `0 12px 30px -16px ${accentColor(55, 0.15, 0.4, accentHue)}`
                    : undefined,
            }}
        >
            <div className="mb-[18px] flex items-center justify-between">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground/80">
                    {feature.code}
                </span>
                <FeatureIcon
                    name={feature.icon}
                    className="h-[22px] w-[22px] text-muted-foreground transition-colors duration-[220ms] group-hover:[color:var(--auth-accent)]"
                />
            </div>
            <div className="mb-1.5 text-[17px] font-medium tracking-[-0.01em] text-foreground">
                {feature.title}
            </div>
            <div className="text-[13px] leading-[1.5] text-muted-foreground">{feature.body}</div>
            {/* Accent sweep bar — animates width on hover. */}
            <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 h-px transition-[width] duration-[320ms]"
                style={{
                    width: hover ? '100%' : '32%',
                    background: `linear-gradient(90deg, ${accentColor(55, 0.18, 1, accentHue)} 0%, ${accentColor(55, 0.18, 0, accentHue)} 100%)`,
                }}
            />
        </div>
    );
}
